import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Helmet } from "react-helmet-async";
import { Copy, Wallet, Coins, Users, MousePointerClick } from "lucide-react";
import { useTranslation } from "@/i18n/translations";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import PayoutDetailsForm from "@/components/PayoutDetailsForm";

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wider mb-3">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className="text-3xl font-bold text-white">{value}</div>
      {sub ? <div className="text-xs text-zinc-500 mt-1">{sub}</div> : null}
    </div>
  );
}

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [applying, setApplying] = useState(false);
  const [applyName, setApplyName] = useState("");
  const [applyCode, setApplyCode] = useState("");

  const [payoutAmount, setPayoutAmount] = useState("");
  // EUR only — payouts are Europe-only (card or IBAN).
  const [payoutCurrency] = useState("EUR");
  const [requestingPayout, setRequestingPayout] = useState(false);

  const currencySymbol = (cur) => {
    if (cur === "EUR") return "€";
    if (cur === "USD") return "$";
    return cur;
  };

  const fmt = (n) => Number(n || 0).toLocaleString(lang === "ru" ? "ru-RU" : "en-US", { maximumFractionDigits: 2 });
  const fmtMoney = (n, cur) => `${fmt(n)} ${currencySymbol(cur || "EUR")}`;

  const fetchAll = useCallback(async () => {
    try {
      const [s, r, p] = await Promise.all([
        axios.get(`${API}/partner/stats`),
        axios.get(`${API}/partner/referrals`),
        axios.get(`${API}/partner/payouts`),
      ]);
      setStats(s.data);
      setReferrals(r.data.referrals || []);
      setPayouts(p.data.payouts || []);
    } catch (err) {
      if (err?.response?.status === 404) {
        setStats(null);
      } else if (err?.response?.status === 401) {
        navigate("/auth");
      } else {
        toast.error(t("partner.toast.error"));
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/auth");
      return;
    }
    fetchAll();
  }, [fetchAll, navigate]);

  const handleBecomePartner = async (e) => {
    e.preventDefault();
    setApplying(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.post(`${API}/partner/apply`, {
        email: user.email,
        full_name: applyName || user.salon_name || user.email,
        desired_ref_code: applyCode || undefined,
      });
      toast.success(t("partner.onboard.success"));
      await fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || t("partner.onboard.error");
      toast.error(typeof msg === "string" ? msg : t("partner.onboard.error"));
    } finally {
      setApplying(false);
    }
  };

  const handleRequestPayout = async (e) => {
    e.preventDefault();
    setRequestingPayout(true);
    try {
      await axios.post(`${API}/partner/payouts/request`, {
        amount: parseFloat(payoutAmount),
        currency: payoutCurrency,
      });
      toast.success(t("partner.dash.request.created"));
      setPayoutAmount("");
      await fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || t("partner.onboard.error");
      toast.error(typeof msg === "string" ? msg : t("partner.onboard.error"));
    } finally {
      setRequestingPayout(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        {t("partner.dash.loading")}
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <Helmet>
          <title>{t("partner.onboard.title")}</title>
        </Helmet>
        <div className="max-w-2xl mx-auto px-6 py-20">
          <h1 className="text-3xl font-bold mb-3">{t("partner.onboard.title")}</h1>
          <p className="text-zinc-400 mb-8">{t("partner.onboard.subtitle")}</p>
          <form onSubmit={handleBecomePartner} className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8" data-testid="partner-onboard-form">
            <label className="text-xs uppercase tracking-wider text-zinc-500">{t("partner.onboard.name")}</label>
            <Input
              value={applyName}
              onChange={(e) => setApplyName(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-white mt-1 mb-4"
              data-testid="partner-onboard-name"
            />
            <label className="text-xs uppercase tracking-wider text-zinc-500">{t("partner.onboard.code")}</label>
            <Input
              value={applyCode}
              onChange={(e) => setApplyCode(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 24))}
              placeholder="ivan42"
              className="bg-zinc-900 border-zinc-800 text-white mt-1 mb-6"
              data-testid="partner-onboard-code"
            />
            <Button
              type="submit"
              disabled={applying}
              className="w-full bg-yellow-300 text-black hover:bg-yellow-200 h-12 font-semibold"
              data-testid="partner-onboard-submit"
            >
              {applying ? "..." : t("partner.onboard.submit")}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  const partner = stats.partner;
  const refUrl = `${window.location.origin}/?ref=${partner.ref_code}`;
  const defaultCur = "EUR";

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Helmet>
        <title>{t("partner.dash.title")}</title>
      </Helmet>

      <div className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} data-testid="partner-logo-btn">
              <img src="/logo-header.png" alt="Chatity" className="h-6 w-auto invert" />
            </button>
            <span className="text-zinc-600">/</span>
            <span className="text-zinc-400">{t("partner.dash.breadcrumb")}</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-white"
              onClick={() => navigate("/dashboard")}
              data-testid="partner-back-to-dashboard"
            >
              {t("partner.dash.backToClient")}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 space-y-10">
        <section className="bg-gradient-to-br from-yellow-300/10 to-zinc-950 border border-yellow-300/20 rounded-2xl p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-yellow-300 mb-1">{t("partner.dash.refLinkLabel")}</div>
              <div className="font-mono text-lg break-all" data-testid="partner-ref-link-display">{refUrl}</div>
              <div className="text-xs text-zinc-500 mt-1">
                {t("partner.dash.commissionInfo", { pct: partner.commission_percent, days: partner.hold_days })}
              </div>
            </div>
            <Button
              onClick={() => { navigator.clipboard.writeText(refUrl); toast.success(t("partner.dash.copied")); }}
              className="bg-yellow-300 text-black hover:bg-yellow-200"
              data-testid="partner-copy-link"
            >
              <Copy className="w-4 h-4 mr-2" /> {t("partner.dash.copy")}
            </Button>
          </div>
        </section>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={MousePointerClick}
            label={t("partner.dash.stat.clicks")}
            value={stats.clicks_total}
            sub={t("partner.dash.stat.clicks30d", { n: stats.clicks_30d })}
            testid="stat-clicks"
          />
          <StatCard
            icon={Users}
            label={t("partner.dash.stat.signups")}
            value={stats.referrals_signed_up}
            sub={t("partner.dash.stat.paid", { n: stats.referrals_paid_count })}
            testid="stat-signups"
          />
          <StatCard
            icon={Coins}
            label={t("partner.dash.stat.earned")}
            value={fmtMoney(stats.total_earned, defaultCur)}
            sub={t("partner.dash.stat.pending", { amount: fmtMoney(stats.balance_pending, defaultCur) })}
            testid="stat-earned"
          />
          <StatCard
            icon={Wallet}
            label={t("partner.dash.stat.available")}
            value={fmtMoney(stats.balance_available, defaultCur)}
            sub={t("partner.dash.stat.paidOut", { amount: fmtMoney(stats.balance_paid, defaultCur) })}
            testid="stat-available"
          />
        </section>

        <section className="grid lg:grid-cols-2 gap-6">
          <PayoutDetailsForm API={API} partner={partner} onSaved={fetchAll} />

          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6">
            <h3 className="text-lg font-semibold mb-1">{t("partner.dash.request.title")}</h3>
            <p className="text-sm text-zinc-500 mb-5">
              {t("partner.dash.request.available")}: <strong className="text-white">{fmtMoney(stats.balance_available, defaultCur)}</strong> • {t("partner.dash.request.min")}
            </p>
            <form onSubmit={handleRequestPayout}>
              <label className="text-xs uppercase tracking-wider text-zinc-500">{t("partner.dash.request.amount")}</label>
              <div className="flex gap-2 mt-1 mb-4">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  required
                  className="bg-zinc-900 border-zinc-800 text-white flex-1"
                  data-testid="payout-amount-input"
                />
                <Button
                  type="button"
                  onClick={() => setPayoutAmount(String(stats.balance_available || 0))}
                  disabled={!stats.balance_available || stats.balance_available <= 0}
                  variant="outline"
                  title={t("partner.dash.request.maxHint")}
                  className="border-zinc-700 text-white hover:bg-zinc-900 px-3 shrink-0"
                  data-testid="payout-max-btn"
                >
                  {t("partner.dash.request.max")}
                </Button>
              </div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">{t("partner.dash.request.currency")}</label>
              <div
                className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white mt-1 mb-4 px-3 py-2 text-sm"
                data-testid="payout-currency-display"
              >
                EUR (€)
              </div>
              <Button
                type="submit"
                disabled={requestingPayout || stats.balance_available <= 0}
                className="bg-yellow-300 text-black hover:bg-yellow-200 w-full"
                data-testid="payout-request-btn"
              >
                {requestingPayout ? "..." : t("partner.dash.request.submit")}
              </Button>
            </form>
          </div>
        </section>

        <section className="bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-zinc-900">
            <h3 className="text-lg font-semibold">{t("partner.dash.referrals.title")}</h3>
          </div>
          {referrals.length === 0 ? (
            <div className="p-10 text-center text-zinc-500" data-testid="no-referrals">
              {t("partner.dash.referrals.empty")}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-900 hover:bg-transparent">
                  <TableHead className="text-zinc-500">{t("partner.dash.referrals.email")}</TableHead>
                  <TableHead className="text-zinc-500">{t("partner.dash.referrals.business")}</TableHead>
                  <TableHead className="text-zinc-500">{t("partner.dash.referrals.plan")}</TableHead>
                  <TableHead className="text-zinc-500">{t("partner.dash.referrals.status")}</TableHead>
                  <TableHead className="text-zinc-500">{t("partner.dash.referrals.payments")}</TableHead>
                  <TableHead className="text-zinc-500 text-right">{t("partner.dash.referrals.earned")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.map((r) => (
                  <TableRow key={r.user_id} className="border-zinc-900" data-testid={`referral-row-${r.user_id}`}>
                    <TableCell className="font-mono text-xs">{r.email}</TableCell>
                    <TableCell>{r.salon_name || "—"}</TableCell>
                    <TableCell>{r.plan || "trial"}</TableCell>
                    <TableCell>
                      {r.is_paid ? (
                        <span className="text-green-400 text-xs">{t("partner.dash.referrals.paid")}</span>
                      ) : (
                        <span className="text-zinc-500 text-xs">{t("partner.dash.referrals.trial")}</span>
                      )}
                    </TableCell>
                    <TableCell>{r.payments_count}</TableCell>
                    <TableCell className="text-right">{fmtMoney(r.commission_total, defaultCur)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>

        <section className="bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-zinc-900">
            <h3 className="text-lg font-semibold">{t("partner.dash.payouts.title")}</h3>
          </div>
          {payouts.length === 0 ? (
            <div className="p-10 text-center text-zinc-500" data-testid="no-payouts">
              {t("partner.dash.payouts.empty")}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-900 hover:bg-transparent">
                  <TableHead className="text-zinc-500">{t("partner.dash.payouts.date")}</TableHead>
                  <TableHead className="text-zinc-500">{t("partner.dash.payouts.amount")}</TableHead>
                  <TableHead className="text-zinc-500">{t("partner.dash.payouts.method")}</TableHead>
                  <TableHead className="text-zinc-500">{t("partner.dash.payouts.status")}</TableHead>
                  <TableHead className="text-zinc-500">{t("partner.dash.payouts.note")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.map((p) => (
                  <TableRow key={p.id} className="border-zinc-900" data-testid={`payout-row-${p.id}`}>
                    <TableCell className="text-sm">{(p.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell className="font-medium">{fmtMoney(p.amount, p.currency)}</TableCell>
                    <TableCell className="text-sm">{p.payout_method}</TableCell>
                    <TableCell>
                      <span className={
                        p.status === "paid" ? "text-green-400 text-xs" :
                        p.status === "rejected" ? "text-red-400 text-xs" :
                        "text-yellow-300 text-xs"
                      }>{p.status}</span>
                    </TableCell>
                    <TableCell className="text-sm text-zinc-500">{p.note || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>
      </div>
    </div>
  );
}
